import { defineElement, FC, useElement } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { Button } from '@atoms/button/button';
import getComponentData from '@atoms/component-data/utils/get-component-data';
import { Image, ImageInterface } from '@atoms/image/image';
import { MediaOverlayInterface } from '@molecules/media-overlay/media-overlay';
import { useStylesheet } from '@utilities/hooks';

import style from './images-grid.scss';
import IMAGE_SETTINGS from './utilities/image-settings';

type GridItemSize = 'third' | 'half' | 'full';
type GridItemSetting = 'small' | 'medium' | 'large' | 'extralarge';

export interface BPDImageGrid extends HTMLElement {}

const ImageGrid: FC = ({ element }) => {
    useStylesheet(style);
    const overlay = useElement<MediaOverlayInterface>('[js-hook-images-grid-overlay]');

    const data = getComponentData<{ images: ImageInterface[] }>(element);
    const images = data?.images;

    function openModal(index?: number) {
        if (overlay.current) {
            overlay.current.open({ index });
        }
    }

    const GridItem = ({
        image,
        size,
        setting,
        onClick,
    }: {
        image: ImageInterface;
        size: GridItemSize;
        setting: GridItemSetting;
        onClick: Function;
    }) => {
        const settings = IMAGE_SETTINGS[setting];

        return (
            <button onClick={() => onClick()} class={`image-grid__item image-grid__item--${size}`}>
                <Image
                    classes="image-grid__image"
                    objectFit={true}
                    image={image.image}
                    preload={{
                        width: 20,
                        height: 20,
                    }}
                    settings={settings}
                    alt={image.alt}
                />
            </button>
        );
    };

    function getGridItemProps(): { size: GridItemSize; setting: GridItemSetting } {
        switch (images?.length) {
            case 1:
                return { size: 'full', setting: 'extralarge' };
            case 3:
                return { size: 'third', setting: 'small' };
            default:
                return { size: 'half', setting: 'medium' };
        }
    }

    if (!images || !images.length) return;

    const mediaOverlayDataStr = JSON.stringify({ images });

    return (
        <Fragment>
            <div class="c-image-grid">
                <div class="image-grid__container">
                    {images.length >= 5 ? (
                        <Fragment>
                            <div className="image-grid__column">
                                <GridItem
                                    image={images[0]}
                                    size="full"
                                    setting="large"
                                    onClick={() => openModal(0)}
                                />
                            </div>
                            <div class="image-grid__column">
                                {[...images].splice(1, 4).map((image, i) => (
                                    <GridItem
                                        image={image}
                                        size="half"
                                        setting="small"
                                        onClick={() => openModal(i + 1)}
                                    />
                                ))}
                            </div>
                        </Fragment>
                    ) : (
                        images.map((image, i) => (
                            <GridItem
                                image={image}
                                onClick={() => openModal(i)}
                                {...getGridItemProps()}
                            />
                        ))
                    )}
                </div>

                {images.length > 5 && (
                    <div className="u-flex u-flex-center">
                        <Button
                            classes="image-grid__button"
                            icon="icons-plus"
                            label={`Bekijk alle ${images.length} foto's`}
                            variant="grey-90"
                            onClick={() => openModal()}
                        />
                    </div>
                )}
            </div>

            <bpd-media-overlay
                modal-id="images-grid-overlay"
                media={mediaOverlayDataStr}
                js-hook-images-grid-overlay
            />
        </Fragment>
    );
};

defineElement('bpd-images-grid', ImageGrid);
